import { useEffect, useRef, useState } from 'react';

type UsePassiveTimerOptions = {
  thresholdMs?: number;
};

type UsePassiveTimerReturn = {
  startCheckSlowResponse: () => void;
  stopCheckSlowResponse: () => void;
  isSlow: boolean;
  isRunning: boolean;
  elapsedMs: number;
};

// It might be good to make this configurable using a LD flag or sanity configuration
// for the moment I would set as 5seconds
const THRESHOLD_MS = 5000;

// It would be great to use this for all requests done, so we can detect pain points due to
// async tasks that might take more time than expected to finish.
export const usePassiveTimer = ({
  thresholdMs = THRESHOLD_MS,
}: UsePassiveTimerOptions = {}): UsePassiveTimerReturn => {
  const [elapsedMs, setElapsedMs] = useState(0);
  const [isSlow, setIsSlow] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef<NodeJS.Timer | null>(null);
  const startRef = useRef<number | null>(null);

  const start = () => {
    if (isRunning) {
      return;
    }

    startRef.current = Date.now();
    setElapsedMs(0);
    setIsSlow(false);
    setIsRunning(true);

    intervalRef.current = setInterval(() => {
      const now = Date.now();
      const elapsed = now - (startRef.current ?? now);
      setElapsedMs(elapsed);
      if (elapsed >= thresholdMs) {
        setIsSlow(true);
      }
    }, 250);
  };

  const stop = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (startRef.current) {
      setElapsedMs(Date.now() - startRef.current);
    }

    setIsRunning(false);
    setIsSlow(false);
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    startCheckSlowResponse: start,
    stopCheckSlowResponse: stop,
    isSlow,
    isRunning,
    elapsedMs,
  };
};
